import { IText, util } from 'fabric';

type FabricObjectWithMultiColorEffect = IText & {
    isMultiColorEffect?: boolean;
    _render_original_multicolor?: (ctx: CanvasRenderingContext2D) => void;
    _offscreenCanvas_multicolor?: HTMLCanvasElement;
    _textureDirty_multicolor?: boolean;
    set_original_multicolor?(key: any, value?: any): FabricObjectWithMultiColorEffect;
};

function customMultiColorRender(this: FabricObjectWithMultiColorEffect, ctx: CanvasRenderingContext2D) {
    if (!this.isMultiColorEffect) {
        this._render_original_multicolor?.(ctx);
        return;
    }

    if (this._textureDirty_multicolor || !this._offscreenCanvas_multicolor) {
        const offscreen = util.createCanvasElement();
        const zoom = 2; // Use supersampling for better quality
        offscreen.width = this.width * zoom;
        offscreen.height = this.height * zoom;
        const octx = offscreen.getContext('2d')!;
        
        octx.scale(zoom, zoom);
        octx.translate(this.width / 2, this.height / 2);
        this._render_original_multicolor?.(octx);
        
        this._offscreenCanvas_multicolor = offscreen;
        this._textureDirty_multicolor = false;
    }

    const offscreen = this._offscreenCanvas_multicolor;
    if (!offscreen) return;
    
    const octx = offscreen.getContext('2d')!;
    const panelW = offscreen.width;
    const panelH = offscreen.height;
    
    const skip = 4; // Pixel gap
    
    ctx.save();
    
    const imageData = octx.getImageData(0, 0, panelW, panelH).data;

    // We draw relative to the object's center (0,0)
    const renderW = this.width;
    const renderH = this.height;

    for (let y = 0; y < panelH; y += skip) {
        for (let x = 0; x < panelW; x += skip) {
            const alphaIndex = (y * panelW + x) * 4 + 3;
            const alpha = imageData[alphaIndex] / 255;

            if (alpha > 0.5) {
                const hue = Math.random() * 360;
                ctx.fillStyle = `hsl(${hue}, 100%, 60%)`;
                
                // Map from offscreen canvas coords to local object coords
                const localX = (x / panelW - 0.5) * renderW;
                const localY = (y / panelH - 0.5) * renderH;

                ctx.beginPath();
                ctx.arc(localX, localY, skip * 0.225, 0, Math.PI * 2);
                ctx.fill();
            }
        }
    }
    ctx.restore();
}

export function enableMultiColor(textObject: IText) {
    const obj = textObject as FabricObjectWithMultiColorEffect;

    if (!obj._render_original_multicolor) {
        obj._render_original_multicolor = obj._render.bind(obj);
        obj._render = customMultiColorRender as any;

        obj.set_original_multicolor = obj.set.bind(obj);
        obj.set = function(this: FabricObjectWithMultiColorEffect, key: any, value?: any) {
            const watchedProps = ['text', 'fontSize', 'fontFamily', 'fontWeight', 'fill', 'stroke', 'strokeWidth', 'textBackgroundColor', 'scaleX', 'scaleY'];
            let dirty = false;
            if (typeof key === 'object') {
                for (const k in key) if (watchedProps.includes(k)) { dirty = true; break; }
            } else if (watchedProps.includes(key)) {
                dirty = true;
            }
            if (dirty) this._textureDirty_multicolor = true;
            this.set_original_multicolor?.(key, value);
            return this;
        }.bind(obj);
    }
    
    obj.isMultiColorEffect = true;
    obj._textureDirty_multicolor = true;
    obj.objectCaching = false;
    obj.dirty = true;
    obj.canvas?.requestRenderAll();
}

export function disableMultiColor(textObject: IText) {
    const obj = textObject as FabricObjectWithMultiColorEffect;

    if (obj._render_original_multicolor) {
        obj._render = obj._render_original_multicolor;
        delete obj._render_original_multicolor;
    }
    if (obj.set_original_multicolor) {
        obj.set = obj.set_original_multicolor;
        delete obj.set_original_multicolor;
    }

    obj.isMultiColorEffect = false;
    delete obj._offscreenCanvas_multicolor;
    obj.objectCaching = true;
    obj.dirty = true;
    obj.canvas?.requestRenderAll();
}
